/**
 * Home dashboard — progress rings (Idea Dashboard %, Learning Hub %),
 * "Book Idea Review" cover card with "Call booked" + "Confidentiality Agreement
 * In Place" badges and booked-call summary, hide-on-scroll pill nav, sign-out.
 * Prototype source: data-page="home". Local state; Firestore wiring in Phase 4.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { ProgressRing } from '../components/ProgressRing';
import { useSession } from '../context/SessionContext';
import { api } from '../lib/api';
import { getAttribution } from '../lib/attribution';
import { computeLearningPct } from '../lib/learningProgress';
import './Home.css';

const ArrowIcon = () => (
  <svg
    className="itk-card-arrow"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export function Home() {
  const navigate = useNavigate();
  const session = useSession();

  // Hydrate session from the BFF on mount.
  useEffect(() => {
    api
      .get<{
        uid: string;
        email: string | null;
        ideas: { id: string; name: string; updatedAt: number; scanPct: number }[];
        alertPreferences: { notifications: boolean; email: boolean };
        celebrations: Record<string, boolean>;
        booking: { coachName: string; coachRole: string; slotLabel: string; status: string } | null;
        confidentialityInPlace: boolean;
        activeIdeaId: string | null;
        learningCompletedModules?: string[];
      }>('/api/v1/me')
      .then((data) => {
        session.setEmail(data.email);
        session.setIdeas(data.ideas);
        if (data.activeIdeaId) session.setActiveIdea(data.activeIdeaId);
        if (data.booking) session.setBooking(data.booking as never);
        session.setConfidentiality(data.confidentialityInPlace);
        if (data.learningCompletedModules && Array.isArray(data.learningCompletedModules)) {
          session.setLearningCompletedModules(data.learningCompletedModules);
          session.setLearningPct(computeLearningPct(data.learningCompletedModules));
        }
        if (data.celebrations?.learn) session.markCelebration('learn');
        if (data.celebrations?.scan) session.markCelebration('scan');
        if (data.celebrations?.booking) session.markCelebration('booking');

        // §12 conversion goal page — on first sign-in (firstSignIn flag absent),
        // redirect to /welcome so Google Ads registers the goal. The goal page
        // marks the flag (once-per-user) and the user lands on Home next time.
        if (!data.celebrations?.firstSignIn) {
          navigate('/welcome', { replace: true });
        }
      })
      .catch(() => {
        // If /me fails (e.g. not authenticated), the ProtectedRoute will redirect.
      });

    // §12 attribution: persist gclid + UTMs + landing URL to the user profile
    // so the BFF has them for the Zoho commit even if the magic-link flow
    // opened a new tab (sessionStorage wouldn't survive) or the user returns
    // on a new device. Sent once after hydration; the BFF keeps the first
    // (first-touch) record.
    const attr = getAttribution();
    if (attr && (attr.gclid || attr.utm_source)) {
      api
        .put('/api/v1/me/attribution', {
          gclid: attr.gclid,
          utm_source: attr.utm_source,
          utm_medium: attr.utm_medium,
          utm_campaign: attr.utm_campaign,
          utm_term: attr.utm_term,
          utm_content: attr.utm_content,
          landingUrl: attr.landingUrl,
          capturedAt: attr.capturedAt,
        })
        .catch(() => {
          // Non-fatal — attribution is best-effort for marketing tracking.
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Idea Dashboard progress — reflects the ACTIVE idea specifically (the one the
  // Idea Dashboard shows), so the two rings always agree. Not an average across
  // all ideas, which would include orphaned duplicates from earlier sessions.
  const activeIdea = session.ideas.find((i) => i.id === session.activeIdeaId);
  const ideaPct = activeIdea ? activeIdea.scanPct : 0;
  const learningPct = session.learningPct;

  const hasBooking = !!session.booking;
  const hasConf = session.confidentialityInPlace;

  const coverTitle = hasBooking ? 'Call booked' : 'Book Idea Review';
  const coverSub = hasBooking
    ? `${session.booking!.coachName} · ${session.booking!.slotLabel}`
    : 'Confidentially review your project with an innovation expert';

  return (
    <>
      <TopNav title="Idea Toolkit" />

      <div className="itk-wrap itk-wrap--home">
        {/* Row 1 — progress ring cards */}
        <div className="itk-dash-grid">
          <button
            className="itk-dash-card itk-card-2col"
            onClick={() => navigate('/learning')}
            aria-label="Open the Learning Hub"
          >
            <div className="itk-c2-main">
              <h3>
                Learning Hub
                <ArrowIcon />
              </h3>
              <p className="itk-card-sub">
                Learn how to patent, prototype, licence and manufacture your product.
              </p>
            </div>
            <div className="itk-c2-ring" role="img" aria-label="Learning Hub progress">
              <ProgressRing percent={learningPct} size={92} stroke={10} label="complete" />
            </div>
          </button>

          <button
            className="itk-dash-card itk-card-2col"
            onClick={() => navigate('/idea')}
            aria-label="Open the Idea Dashboard"
          >
            <div className="itk-c2-main">
              <h3>
                Idea Dashboard
                <ArrowIcon />
              </h3>
              <p className="itk-card-sub">
                A selection of tools to help you decide whether your idea has legs.
              </p>
            </div>
            <div className="itk-c2-ring" role="img" aria-label="Idea Dashboard progress">
              <ProgressRing percent={ideaPct} size={92} stroke={10} label="complete" />
            </div>
          </button>
        </div>

        {/* Row 2 — Book Idea Review cover card */}
        <div className="itk-dash-grid itk-dash-grid--full">
          <button
            className="itk-card-cover"
            onClick={() => navigate('/book')}
            aria-label="Book idea review"
          >
            <span className="itk-cover-scrim">
              <span>
                {hasBooking && <span className="itk-cover-badge">Call booked</span>}
                {hasConf && (
                  <span className="itk-cover-badge itk-cover-badge--conf">
                    Confidentiality Agreement In Place
                  </span>
                )}
                <h3>{coverTitle}</h3>
                <span className="itk-cover-sub">{coverSub}</span>
              </span>
              <ArrowIcon />
            </span>
          </button>
        </div>

        {/* Row 3 — Alert Centre + Settings */}
        <div className="itk-dash-grid itk-dash-grid--split32">
          <button
            className="itk-dash-card itk-card-2col"
            onClick={() => navigate('/alerts')}
            aria-label="Open Alert Centre"
          >
            <div className="itk-c2-main">
              <h3>
                Alert Centre
                <ArrowIcon />
              </h3>
              <p className="itk-card-sub">
                Be the first to find out about funding and tips and new features.
              </p>
            </div>
            <div className="itk-c2-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
          </button>

          <button
            className="itk-dash-card itk-card-2col"
            onClick={() => navigate('/settings')}
            aria-label="Open Settings"
          >
            <div className="itk-c2-main">
              <h3>
                Settings
                <ArrowIcon />
              </h3>
              <p className="itk-card-sub">Manage your email address and account.</p>
            </div>
            <div className="itk-c2-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </div>
          </button>
        </div>
      </div>
    </>
  );
}